import React, { useState, useEffect, useContext } from "react"
import {
  Button,
  Col,
  Form,
  Input,
  Label,
  Row,
  UncontrolledAlert,
  Card,
  CardBody,
  Modal,
} from "reactstrap"
import TenantConnectionContext from "./TenantConnectionContext"
import LoadingOverlay from "react-loading-overlay"
import Error from "pages/NotificationModal/Error"
import Success from "./Success"
import Confirmation from "./Confirmation"
import SecretInput from "./SecretInput"

// MC_API (MotorCity) outbound auth: an "Api-Key" header carrying the token, and a
// per-appointment-status webhook URL (config.sts_urls). The api_v1 outbound module
// leaves the base url null and dispatches per status. This form edits exactly
// those two things; header name (Api-Key), method, and username stay as-is.
const MC_API_STATUS_CODES = [
  { code: "QPND", label: "QPND — Pending" },
  { code: "QREQ", label: "QREQ — Requested" },
  { code: "QISU", label: "QISU — Exception" },
  { code: "QCON", label: "QCON — Confirmed" },
]

// Matches the exact masked shapes auth_schema emits ("*", "**", "X**Y"); a masked
// value on submit means "keep the stored secret".
const isMasked = v => typeof v === "string" && /^(\*{1,2}|.\*\*.)$/.test(v)
const hasVal = v => v != null && String(v).trim() !== ""

const McApiAuthForm = ({ hideForm }) => {
  const {
    loading,
    success,
    error,
    errorMessage,
    setSuccess,
    setError,
    tenantData,
    submitUpdate,
    revealSecret,
    updateResponse,
  } = useContext(TenantConnectionContext)

  const [token, setToken] = useState("")
  const [stsUrls, setStsUrls] = useState({})
  const [errorMsg, setErrorMsg] = useState("")
  const [showConfirmation, setShowConfirmation] = useState(false)

  useEffect(() => {
    if (!tenantData) return
    setToken(tenantData.token || "")
    const sts = tenantData.sts_urls || (tenantData.advanced || {}).sts_urls || {}
    setStsUrls({ ...sts })
  }, [tenantData])

  const setStsUrl = (code, value) =>
    setStsUrls(prev => ({ ...prev, [code]: value }))

  const fieldRow = (label, node) => (
    <Row className="mb-2" key={label}>
      <Label className="col-sm-3 col-form-label">{label}</Label>
      <Col sm={6} className="ms-4">
        {node}
      </Col>
    </Row>
  )

  const handleSubmit = () => {
    setErrorMsg("")
    if (!(hasVal(token) || isMasked(token))) {
      setErrorMsg("API Key is required")
      return
    }
    const missing = MC_API_STATUS_CODES.filter(
      s => !hasVal(stsUrls[s.code])
    ).map(s => s.code)
    if (missing.length) {
      setErrorMsg(`Webhook URL required for: ${missing.join(", ")}`)
      return
    }
    setShowConfirmation(true)
  }

  const handleConfirmSubmit = async () => {
    setShowConfirmation(false)
    const sts_urls = {}
    MC_API_STATUS_CODES.forEach(s => {
      sts_urls[s.code] = (stsUrls[s.code] || "").trim()
    })
    await submitUpdate({ token, sts_urls })
  }

  const handleCancel = () => {
    setShowConfirmation(false)
    hideForm()
  }
  const closeSuccess = () => {
    setSuccess(false)
    hideForm()
  }
  const togError = () => setError(false)

  if (!tenantData) {
    return (
      <LoadingOverlay active={loading} spinner>
        <div style={{ textAlign: "center", padding: "40px" }}>
          <h5>Loading...</h5>
        </div>
      </LoadingOverlay>
    )
  }

  return (
    <>
      <Modal isOpen={success} toggle={closeSuccess} centered size="md">
        <Success data={updateResponse} cancel={closeSuccess} />
      </Modal>
      <Modal isOpen={error} toggle={togError} centered size="sm">
        <Error
          message={errorMessage || "Something went wrong"}
          closeModal={togError}
        />
      </Modal>
      <Modal
        isOpen={showConfirmation}
        toggle={() => setShowConfirmation(false)}
        centered
        size="sm"
      >
        <Confirmation
          message="Do you want to save these changes?"
          confirm={handleConfirmSubmit}
          cancel={() => setShowConfirmation(false)}
        />
      </Modal>

      <LoadingOverlay active={loading} spinner>
        <Card className="p-3">
          <div className="mb-3">
            <h6 className="text-primary">
              Edit MotorCity (MC_API) Outbound Auth
            </h6>
          </div>
          <CardBody className="p-0">
            <Card outline color="primary" className="border mt-2 mb-3">
              <CardBody className="p-3">
                {errorMsg && (
                  <div className="mb-3">
                    <UncontrolledAlert color="danger" role="alert">
                      {errorMsg}
                    </UncontrolledAlert>
                  </div>
                )}
                <Form>
                  {fieldRow(
                    "API Key (Api-Key header)",
                    <SecretInput
                      value={token}
                      onChange={setToken}
                      label="API Key"
                      placeholder="Enter api key"
                      revealPath="token"
                      onReveal={revealSecret}
                    />
                  )}

                  <Row className="mb-1 mt-3">
                    <Col sm={9} className="ms-4">
                      <small className="text-muted">
                        Per-status webhook URLs — MotorCity delivers each
                        appointment status to its own endpoint.
                      </small>
                    </Col>
                  </Row>
                  {MC_API_STATUS_CODES.map(s =>
                    fieldRow(
                      s.label,
                      <Input
                        type="text"
                        value={stsUrls[s.code] || ""}
                        placeholder={`https://… (${s.code})`}
                        onChange={e => setStsUrl(s.code, e.target.value)}
                      />
                    )
                  )}
                </Form>
              </CardBody>
            </Card>

            <div className="d-flex justify-content-end mt-3 me-4">
              <div className="d-flex gap-2">
                <Button onClick={handleSubmit} className="btn" color="primary">
                  Save
                </Button>
                <Button
                  onClick={handleCancel}
                  className="btn"
                  color="secondary"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </CardBody>
        </Card>
      </LoadingOverlay>
    </>
  )
}

export default McApiAuthForm
